import { RefObject, useEffect } from 'react'

import { toCalendarDataDate } from 'shared/lib/calendarDateUtils'

const CALENDAR_DAY_HEIGHT = 44

export interface CalendarFocusOptions {
  headerRef?: RefObject<HTMLElement | null>
  scrollContainerRef?: RefObject<HTMLElement | null>
  offset?: number
}

const scrollToCalendarDate = (
  focusDate: string,
  options?: CalendarFocusOptions,
): boolean => {
  const normalizedDate = toCalendarDataDate(focusDate)

  if (!normalizedDate) return false

  const dayElement = document.querySelector(
    `[data-calendar-date="${normalizedDate}"]`,
  )

  if (!dayElement) return false

  const headerHeight = options?.headerRef?.current?.offsetHeight ?? 0
  const totalOffset = headerHeight + (options?.offset ?? CALENDAR_DAY_HEIGHT)
  const container = options?.scrollContainerRef?.current

  if (container) {
    const containerRect = container.getBoundingClientRect()
    const elementRect = dayElement.getBoundingClientRect()
    const scrollTop =
      container.scrollTop + (elementRect.top - containerRect.top) - totalOffset

    container.scrollTo({
      behavior: 'smooth',
      top: Math.max(0, scrollTop),
    })

    return true
  }

  const top =
    dayElement.getBoundingClientRect().top + window.scrollY - totalOffset

  window.scrollTo({
    behavior: 'smooth',
    top: Math.max(0, top),
  })

  return true
}

const scheduleCalendarFocus = (
  focusDate: string,
  options?: CalendarFocusOptions,
) => {
  if (scrollToCalendarDate(focusDate, options)) return

  requestAnimationFrame(() => {
    if (scrollToCalendarDate(focusDate, options)) return

    window.setTimeout(() => {
      scrollToCalendarDate(focusDate, options)
    }, 50)
  })
}

export const useCalendarFocus = (
  focusDate?: string,
  options?: CalendarFocusOptions,
) => {
  const headerRef = options?.headerRef
  const scrollContainerRef = options?.scrollContainerRef
  const offset = options?.offset

  useEffect(() => {
    scheduleCalendarFocus(focusDate || new Date().toISOString(), {
      headerRef,
      scrollContainerRef,
      offset,
    })
  }, [focusDate, headerRef, scrollContainerRef, offset])
}
